/**
 * Almacenamiento — panel del object store (s3-api).
 *
 * Vivía como un tab dentro de "Configuración IA" y salió de ahí: el
 * almacenamiento de objetos no tiene nada que ver con la IA. Comparten que solo
 * los ve el superadmin del sistema, no el dominio.
 *
 * Todo el acceso pasa por el BFF con el login existente (nunca por URLs
 * públicas ni credenciales aparte). Hoy es lectura + dos escrituras aditivas
 * (crear bucket y subir archivo): borrar, las API keys y el público/privado no
 * están construidos y el backend responde 405.
 *
 * Usa su propio axios (src/services/s3AdminService) sin el interceptor global
 * de 401 → logout, para que un fallo del object store no mate la sesión.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import {
  AlertTriangle,
  Database,
  File as FileIcon,
  FolderOpen,
  HardDrive,
  KeyRound,
  Plus,
  RefreshCw,
  Upload,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { s3AdminService, s3PublicUrl } from "../../../services/s3AdminService";
import { showToast } from "../../../utils/showToast";
import { PageHeader } from "../../../components/ui/PageHeader";
import { SectionCard } from "../../../components/ui/SectionCard";
import { Input } from "../../../components/ui/Input";
import { Button } from "../../../components/ui/Button";
import { Badge } from "../../../components/ui/Badge";
import { Skeleton } from "../../../components/ui/Skeleton";
import { Modal } from "../../../components/ui/Modal";

// ── Utilidades de presentación ───────────────────────────────────────────────
// Copiadas del panel de IA a propósito: son cuatro formateadores y tres
// componentes de diez líneas, y duplicarlos es más barato que dejar las dos
// vistas atadas por un módulo compartido cuando justamente se están separando.

function fmtDate(v, t) {
  if (!v) return t("Nunca");
  const d = dayjs(v);
  return d.isValid() ? d.format("DD-MM-YYYY HH:mm") : String(v);
}

function fmtBytes(bytes) {
  const n = Number(bytes);
  if (bytes == null || Number.isNaN(n)) return "—";
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${n} B`;
}

const numFmt = (v) => (v == null || Number.isNaN(Number(v)) ? "—" : Number(v).toLocaleString("es-CL"));

// Todo el acceso a objetos va POR EL BFF autenticado (JWT) — nunca por la URL
// pública de s3-api directamente. El BFF resuelve la credencial de servicio.
const isImage = (obj) => (obj?.content_type || "").startsWith("image/");

function Field({ label, hint, error, className = "", children }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <label className="text-sm font-medium text-fg">{label}</label>
      {children}
      {error ? (
        <span className="text-xs text-status-error">{error}</span>
      ) : hint ? (
        <span className="text-xs text-fg-muted">{hint}</span>
      ) : null}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-bg p-3">
      <div className="text-xs text-fg-muted">{label}</div>
      <div className="text-xl font-semibold text-fg tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function InlineError({ message, onRetry, t }) {
  return (
    <div className="flex flex-col items-start gap-3 rounded-lg border border-border bg-status-error-soft p-4">
      <div className="flex items-start gap-2 text-sm text-status-error">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
        <span>{message}</span>
      </div>
      <Button variant="outline" size="sm" onClick={onRetry} iconLeft={<RefreshCw size={14} />}>
        {t("Reintentar")}
      </Button>
    </div>
  );
}

function SectionsSkeleton() {
  return (
    <div className="w-full min-w-0 flex flex-col gap-5">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="rounded-lg border border-border bg-bg-muted p-5 space-y-4 shadow-card">
          <Skeleton className="h-9 w-52" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {Array.from({ length: 4 }).map((__, j) => (
              <Skeleton key={j} className="h-10 w-full" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Object store ─────────────────────────────────────────────────────────────

function s3ErrMsg(err, t) {
  const status = err?.response?.status;
  if (status === 401 || status === 403) return t("No tienes permiso para administrar el object store.");
  if (status === 400) return t("Datos inválidos: revisa los valores.");
  if (status === 409) return t("El bucket no está vacío: borra los objetos primero.");
  if (status === 413) return t("El archivo es muy grande.");
  if (status === 503) return t("El servicio de archivos no está disponible. Reintenta más tarde.");
  return t("No se pudo conectar con el object store. Reintenta.");
}

// Thumbnail de un objeto de un bucket PÚBLICO, servido por su URL pública
// (los buckets public_read son públicos por diseño; no lleva credenciales).
function S3Thumb({ bucket, objectKey }) {
  return (
    <div className="w-10 h-10 rounded bg-bg-subtle overflow-hidden shrink-0">
      <img
        src={s3PublicUrl(bucket, objectKey)}
        alt=""
        loading="lazy"
        className="w-full h-full object-cover dark-safe-img"
        onError={(e) => {
          e.currentTarget.style.visibility = "hidden";
        }}
      />
    </div>
  );
}

// Modal de creación de bucket (POST /panel/buckets {name}).
function CreateBucketModal({ open, onClose, onCreated }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setName("");
  }, [open]);

  const trimmed = name.trim();
  // Regla del contrato: minúsculas/dígitos/guiones, 3–63, sin guion al inicio/fin.
  const invalid = trimmed.length > 0 && !/^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/.test(trimmed);

  const submit = async () => {
    if (!trimmed || invalid || saving) return;
    setSaving(true);
    try {
      await s3AdminService.createBucket(trimmed);
      showToast(t("Bucket creado"), true);
      onCreated();
      onClose();
    } catch (err) {
      showToast(s3ErrMsg(err, t), false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={saving ? undefined : onClose}
      title={t("Crear bucket")}
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t("Cancelar")}
          </Button>
          <Button variant="primary" onClick={submit} loading={saving} disabled={!trimmed || invalid}>
            {t("Crear")}
          </Button>
        </>
      }
    >
      <Field
        label={t("Nombre del bucket")}
        hint={t("Minúsculas, números y guion. 3–63 caracteres, sin guion al inicio/fin.")}
        error={invalid ? t("Nombre inválido.") : undefined}
      >
        <Input
          value={name}
          onChange={(e) => setName(e.target.value.toLowerCase())}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="mis-archivos"
          invalid={!!invalid}
          className="font-mono"
        />
      </Field>
    </Modal>
  );
}

function BucketObjects({ bucket, maxUpload, onMutated }) {
  const { t } = useTranslation();
  const [prefix, setPrefix] = useState("");
  const [objects, setObjects] = useState([]);
  const [marker, setMarker] = useState("");
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const isPublic = !!bucket.public_read;

  const fetchPage = useCallback(
    async ({ append, mk, pfx }) => {
      setLoading(true);
      try {
        const res = await s3AdminService.getBucketObjects(bucket.name, {
          prefix: pfx || undefined,
          limit: 100,
          marker: append ? mk || undefined : undefined,
        });
        const list = res?.objects ?? [];
        setObjects((prev) => (append ? [...prev, ...list] : list));
        setMarker(res?.next_marker ?? "");
        setTruncated(!!res?.is_truncated);
      } catch {
        showToast(t("No se pudieron cargar los objetos."), false);
      } finally {
        setLoading(false);
      }
    },
    [bucket.name, t],
  );

  // Cargar la primera página al abrir/cambiar de bucket.
  useEffect(() => {
    setPrefix("");
    fetchPage({ append: false, pfx: "" });
  }, [fetchPage]);

  // Subir un archivo como body crudo. path = prefijo actual (carpeta) + nombre.
  const doUpload = useCallback(
    async (file) => {
      if (!file || uploading) return;
      if (maxUpload && file.size > maxUpload) {
        showToast(t("El archivo supera el límite ({{max}}).", { max: fmtBytes(maxUpload) }), false);
        return;
      }
      setUploading(true);
      try {
        const folder = prefix.replace(/^\/+|\/+$/g, "");
        const path = (folder ? `${folder}/` : "") + file.name;
        await s3AdminService.uploadObject(bucket.name, path, file, file.type);
        showToast(t("Archivo subido"), true);
        await fetchPage({ append: false, pfx: prefix });
        onMutated?.();
      } catch (err) {
        showToast(s3ErrMsg(err, t), false);
      } finally {
        setUploading(false);
      }
    },
    [bucket.name, prefix, maxUpload, uploading, fetchPage, onMutated, t],
  );

  const onFileInput = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    doUpload(f);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    doUpload(e.dataTransfer?.files?.[0]);
  };

  return (
    <>
      <div className="flex gap-2 flex-wrap items-center">
        <Input
          value={prefix}
          onChange={(e) => setPrefix(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && fetchPage({ append: false, pfx: prefix })}
          placeholder={t("Filtrar por prefijo…")}
          className="max-w-xs font-mono"
          disabled={loading}
        />
        <Button variant="outline" onClick={() => fetchPage({ append: false, pfx: prefix })} loading={loading}>
          {t("Filtrar")}
        </Button>
        <div className="flex-1" />
        <input ref={fileRef} type="file" className="hidden" onChange={onFileInput} />
        <Button variant="primary" onClick={() => fileRef.current?.click()} loading={uploading} iconLeft={<Upload size={16} />}>
          {t("Subir archivo")}
        </Button>
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!dragOver) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`rounded-lg border bg-bg divide-y divide-border transition-colors ${dragOver ? "border-brand-500 border-dashed" : "border-border"}`}
      >
        {dragOver && (
          <div className="px-3 py-6 text-sm text-brand-600 text-center">{t("Suelta el archivo para subirlo")}</div>
        )}
        {objects.map((o) => (
          <div key={o.key} className="flex items-center gap-3 px-3 py-2">
            {isPublic && isImage(o) ? (
              <S3Thumb bucket={bucket.name} objectKey={o.key} />
            ) : (
              <div className="w-10 h-10 rounded bg-bg-subtle flex items-center justify-center shrink-0">
                <FileIcon className="w-4 h-4 text-fg-subtle" aria-hidden="true" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="font-mono text-xs text-fg truncate">{o.key}</div>
              <div className="text-xs text-fg-muted truncate">
                {fmtBytes(o.size)} · {o.content_type || "—"} · {fmtDate(o.last_modified, t)}
              </div>
            </div>
            {isPublic && (
              <a
                href={s3PublicUrl(bucket.name, o.key)}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-brand-600 hover:underline shrink-0"
              >
                {t("Abrir")}
              </a>
            )}
          </div>
        ))}
        {!objects.length && !loading && (
          <div className="px-3 py-6 text-sm text-fg-muted text-center">{t("Sin objetos.")}</div>
        )}
        {loading && !objects.length && (
          <div className="p-3 space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        )}
      </div>

      {truncated && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => fetchPage({ append: true, mk: marker, pfx: prefix })} loading={loading}>
            {t("Cargar más")}
          </Button>
        </div>
      )}
    </>
  );
}

function KeysCard({ keys }) {
  const { t } = useTranslation();
  if (!keys?.length) return null;
  return (
    <SectionCard
      icon={<KeyRound className="w-4 h-4" aria-hidden="true" />}
      title={t("Access keys")}
      subtitle={t("Claves de acceso al object store (sin secretos).")}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-fg-muted border-b border-border">
              <th className="py-2 pr-3 font-medium">{t("Nombre")}</th>
              <th className="py-2 px-3 font-medium">{t("Access key ID")}</th>
              <th className="py-2 pl-3 font-medium whitespace-nowrap">{t("Creado")}</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k, i) => (
              <tr key={k.access_key_id || k.name || i} className="border-b border-border last:border-0">
                <td className="py-2 pr-3 text-fg">{k.name || "—"}</td>
                <td className="py-2 px-3 font-mono text-fg-muted">{k.access_key_id || "—"}</td>
                <td className="py-2 pl-3 text-fg-muted whitespace-nowrap">{fmtDate(k.created_at, t)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

function SeccionAlmacenamiento() {
  const { t } = useTranslation();
  const [info, setInfo] = useState(null);
  const [buckets, setBuckets] = useState([]);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showCreateBucket, setShowCreateBucket] = useState(false);

  const load = useCallback(async (soft) => {
    soft ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      const [i, b, k] = await Promise.all([
        s3AdminService.getServerInfo(),
        s3AdminService.getBuckets(),
        s3AdminService.getKeys().catch(() => []),
      ]);
      setInfo(i);
      setBuckets(Array.isArray(b) ? b : b?.buckets ?? []);
      setKeys(Array.isArray(k) ? k : k?.keys ?? []);
    } catch (err) {
      setError(err);
    } finally {
      soft ? setRefreshing(false) : setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  if (loading) return <SectionsSkeleton />;
  if (error)
    return (
      <SectionCard icon={<HardDrive className="w-4 h-4" aria-hidden="true" />} title={t("Almacenamiento")}>
        <InlineError message={s3ErrMsg(error, t)} onRetry={() => load(false)} t={t} />
      </SectionCard>
    );

  const usedPct = info?.disk_total ? Math.min(100, Math.round((info.disk_used / info.disk_total) * 100)) : 0;

  return (
    <div className="w-full min-w-0 flex flex-col gap-5">
      {/* Disco / uso */}
      <SectionCard
        icon={<HardDrive className="w-4 h-4" aria-hidden="true" />}
        title={t("Almacenamiento")}
        subtitle={t("Uso de disco del object store (solo lectura).")}
        action={
          <Button variant="ghost" size="sm" onClick={() => load(true)} loading={refreshing} iconLeft={<RefreshCw size={14} />}>
            {t("Actualizar")}
          </Button>
        }
      >
        {info && (
          <>
            <div>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-fg-muted">{t("Disco")}</span>
                <span className="text-fg tabular-nums">
                  {fmtBytes(info.disk_used)} / {fmtBytes(info.disk_total)} ({usedPct}%)
                </span>
              </div>
              <div className="h-2 rounded-full bg-bg-subtle overflow-hidden">
                <div
                  className={`h-full rounded-full ${usedPct >= 90 ? "bg-status-error" : usedPct >= 75 ? "bg-status-warning" : "bg-brand-500"}`}
                  style={{ width: `${usedPct}%` }}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label={t("Disponible")} value={fmtBytes(info.disk_available)} />
              <Stat label={t("Objetos")} value={numFmt(info.total_objects)} />
              <Stat label={t("Tamaño total")} value={fmtBytes(info.total_size)} />
              <Stat label={t("Subida máx.")} value={fmtBytes(info.max_upload_size)} />
            </div>
            <div className="text-xs text-fg-muted">
              {t("Raíz")}: <span className="font-mono">{info.blobs_root || "—"}</span>
              {info.version ? ` · v${info.version}` : ""}
            </div>
          </>
        )}
      </SectionCard>

      {/* Buckets */}
      <SectionCard
        icon={<Database className="w-4 h-4" aria-hidden="true" />}
        title={t("Buckets")}
        subtitle={t("Contenedores del object store. Selecciona uno para ver sus objetos.")}
        action={
          <Button variant="primary" size="sm" onClick={() => setShowCreateBucket(true)} iconLeft={<Plus size={16} />}>
            {t("Crear bucket")}
          </Button>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-fg-muted border-b border-border">
                <th className="py-2 pr-3 font-medium">{t("Nombre")}</th>
                <th className="py-2 px-3 font-medium text-right">{t("Objetos")}</th>
                <th className="py-2 px-3 font-medium text-right">{t("Tamaño")}</th>
                <th className="py-2 px-3 font-medium">{t("Acceso")}</th>
                <th className="py-2 pl-3 font-medium whitespace-nowrap">{t("Creado")}</th>
              </tr>
            </thead>
            <tbody>
              {buckets.map((b) => (
                <tr
                  key={b.name}
                  onClick={() => setSelected(selected?.name === b.name ? null : b)}
                  className={`border-b border-border last:border-0 cursor-pointer hover:bg-bg-muted transition-colors ${selected?.name === b.name ? "bg-bg-muted" : ""}`}
                >
                  <td className="py-2 pr-3 font-mono text-fg">{b.name}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-fg-muted">{numFmt(b.object_count)}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-fg-muted">{fmtBytes(b.total_size)}</td>
                  <td className="py-2 px-3">
                    <Badge tone={b.public_read ? "info" : "neutral"} dot>
                      {b.public_read ? t("Público") : t("Privado")}
                    </Badge>
                  </td>
                  <td className="py-2 pl-3 text-fg-muted whitespace-nowrap">{fmtDate(b.created_at, t)}</td>
                </tr>
              ))}
              {!buckets.length && (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-fg-muted">
                    {t("Sin buckets.")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Objetos del bucket seleccionado */}
      {selected && (
        <SectionCard
          icon={<FolderOpen className="w-4 h-4" aria-hidden="true" />}
          title={t("Objetos · {{bucket}}", { bucket: selected.name })}
          subtitle={t("Explorador de objetos (paginado).")}
          action={
            <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
              {t("Cerrar")}
            </Button>
          }
        >
          <BucketObjects
            key={selected.name}
            bucket={selected}
            maxUpload={info?.max_upload_size}
            onMutated={() => load(true)}
          />
        </SectionCard>
      )}

      {/* Access keys (solo lectura — sin secretos) */}
      <KeysCard keys={keys} />

      <CreateBucketModal
        open={showCreateBucket}
        onClose={() => setShowCreateBucket(false)}
        onCreated={() => load(true)}
      />
    </div>
  );
}


// ── Panel ────────────────────────────────────────────────────────────────────

export default function AlmacenamientoView() {
  const { t } = useTranslation("calidadia");

  return (
    <div className="w-full min-w-0 p-4 sm:p-6 space-y-4">
      <PageHeader
        icon={<Database className="w-6 h-6 text-fg-muted" aria-hidden="true" />}
        title={t("Almacenamiento")}
        subtitle={t("Object store de la plataforma: buckets, objetos y espacio en disco.")}
      />
      <SeccionAlmacenamiento />
    </div>
  );
}
